import { FunctionCallCache } from './functionCallCache';
import { FunctionLocationTracker } from './functionLocationTracker';
export class FormulaUnlinker {
    static instance = null;
    static getInstance() {
        return this.instance ?? (this.instance = new FormulaUnlinker());
    }
    cache;
    tracker;
    /**
     * Notes are mandatory, but still chunked so one bad note does not fail the whole sheet/workbook.
     */
    NOTE_CHUNK_SIZE = 500;
    /**
     * Verification is intentionally disabled for performance.
     * If values are written successfully and ctx.sync() succeeds, the formula cells targeted by
     * registered mappings have been frozen.
     */
    ENABLE_VERIFICATION = false;
    constructor() {
        this.cache = FunctionCallCache.getInstance();
        this.tracker = FunctionLocationTracker.getInstance();
    }
    async unlinkSelectedRangeAlphaFormulas() {
        await this.tracker.cleanup();
        const summary = this.createSummary();
        const startedAt = Date.now();
        const mappingsToRemove = [];
        try {
            await Excel.run(async (ctx) => {
                const sheet = ctx.workbook.worksheets.getActiveWorksheet();
                const selected = ctx.workbook.getSelectedRange();
                sheet.load('name');
                selected.load(['rowCount', 'columnCount', 'rowIndex', 'columnIndex']);
                await ctx.sync();
                const bounds = {
                    rowIndex: selected.rowIndex,
                    columnIndex: selected.columnIndex,
                    rowCount: selected.rowCount,
                    columnCount: selected.columnCount,
                };
                const collectStartedAt = Date.now();
                const trackerSnapshots = this.getTrackerLocationSnapshotsForSheet(sheet.name);
                let candidates;
                if (trackerSnapshots !== null) {
                    const selectedSnapshots = trackerSnapshots.filter((snapshot) => this.isCellAddressInsideBounds(snapshot.cellAddress, bounds));
                    candidates = await this.collectRegisteredCandidatesFromTrackerSnapshots(ctx, sheet, sheet.name, selectedSnapshots, summary);
                }
                else {
                    selected.load(['formulas', 'values']);
                    await ctx.sync();
                    candidates = this.collectRegisteredCandidatesFromLoadedRange(sheet.name, selected, summary);
                }
                const jobs = this.buildFreezeJobsFromRegisteredCandidates(candidates, summary);
                summary.timingsMs.fastPath += Date.now() - collectStartedAt;
                await this.applyFreezeJobs(ctx, jobs, summary, mappingsToRemove);
                if (this.ENABLE_VERIFICATION) {
                    await this.verifyRangeIfEnabled(ctx, sheet.name, selected, summary);
                }
            });
        }
        finally {
            summary.timingsMs.total = Date.now() - startedAt;
            this.cleanupTrackerMappings(mappingsToRemove);
        }
        return this.finalizeSummary(summary);
    }
    async unlinkActiveWorksheetAlphaFormulas() {
        await this.tracker.cleanup();
        const summary = this.createSummary();
        const startedAt = Date.now();
        const mappingsToRemove = [];
        try {
            await Excel.run(async (ctx) => {
                const sheet = ctx.workbook.worksheets.getActiveWorksheet();
                sheet.load('name');
                await ctx.sync();
                const collectStartedAt = Date.now();
                const trackerSnapshots = this.getTrackerLocationSnapshotsForSheet(sheet.name);
                let candidates;
                if (trackerSnapshots !== null) {
                    if (trackerSnapshots.length === 0) {
                        summary.timingsMs.fastPath += Date.now() - collectStartedAt;
                        return;
                    }
                    candidates = await this.collectRegisteredCandidatesFromTrackerSnapshots(ctx, sheet, sheet.name, trackerSnapshots, summary);
                }
                else {
                    const usedRange = sheet.getUsedRangeOrNullObject();
                    usedRange.load('isNullObject');
                    await ctx.sync();
                    if (usedRange.isNullObject) {
                        summary.status = 'complete';
                        return;
                    }
                    usedRange.load([
                        'rowCount',
                        'columnCount',
                        'rowIndex',
                        'columnIndex',
                        'formulas',
                        'values',
                    ]);
                    await ctx.sync();
                    candidates = this.collectRegisteredCandidatesFromLoadedRange(sheet.name, usedRange, summary);
                    if (this.ENABLE_VERIFICATION) {
                        await this.verifyRangeIfEnabled(ctx, sheet.name, usedRange, summary);
                    }
                }
                const jobs = this.buildFreezeJobsFromRegisteredCandidates(candidates, summary);
                summary.timingsMs.fastPath += Date.now() - collectStartedAt;
                await this.applyFreezeJobs(ctx, jobs, summary, mappingsToRemove);
            });
        }
        finally {
            summary.timingsMs.total = Date.now() - startedAt;
            this.cleanupTrackerMappings(mappingsToRemove);
        }
        return this.finalizeSummary(summary);
    }
    async unlinkWorkbookAlphaFormulas() {
        await this.tracker.cleanup();
        const summary = this.createSummary();
        const startedAt = Date.now();
        await Excel.run(async (ctx) => {
            const worksheets = ctx.workbook.worksheets;
            worksheets.load('items/name');
            await ctx.sync();
            for (const sheet of worksheets.items) {
                const sheetMappingsToRemove = [];
                try {
                    const collectStartedAt = Date.now();
                    const trackerSnapshots = this.getTrackerLocationSnapshotsForSheet(sheet.name);
                    let candidates;
                    if (trackerSnapshots !== null) {
                        if (trackerSnapshots.length === 0) {
                            summary.timingsMs.fastPath += Date.now() - collectStartedAt;
                            continue;
                        }
                        candidates = await this.collectRegisteredCandidatesFromTrackerSnapshots(ctx, sheet, sheet.name, trackerSnapshots, summary);
                    }
                    else {
                        const usedRange = sheet.getUsedRangeOrNullObject();
                        usedRange.load('isNullObject');
                        await ctx.sync();
                        if (usedRange.isNullObject) {
                            continue;
                        }
                        usedRange.load([
                            'rowCount',
                            'columnCount',
                            'rowIndex',
                            'columnIndex',
                            'formulas',
                            'values',
                        ]);
                        await ctx.sync();
                        candidates = this.collectRegisteredCandidatesFromLoadedRange(sheet.name, usedRange, summary);
                        if (this.ENABLE_VERIFICATION) {
                            await this.verifyRangeIfEnabled(ctx, sheet.name, usedRange, summary);
                        }
                    }
                    const jobs = this.buildFreezeJobsFromRegisteredCandidates(candidates, summary);
                    summary.timingsMs.fastPath += Date.now() - collectStartedAt;
                    await this.applyFreezeJobs(ctx, jobs, summary, sheetMappingsToRemove);
                }
                catch (error) {
                    summary.skipped.push({
                        sheet: sheet.name,
                        address: '',
                        reason: 'sheet_unlink_failed',
                        message: `Failed to unlink sheet: ${this.errorToMessage(error)}`,
                    });
                }
                finally {
                    this.cleanupTrackerMappings(sheetMappingsToRemove);
                }
            }
        });
        summary.timingsMs.total = Date.now() - startedAt;
        return this.finalizeSummary(summary);
    }
    /**
     * Fast path.
     *
     * This uses tracker locations directly when FunctionLocationTracker exposes:
     *
     *   getLocationsForSheet(sheetName)
     *
     * or:
     *
     *   getAllLocations()
     *
     * If neither method exists, the unlinker falls back to the older used-range scan.
     */
    getTrackerLocationSnapshotsForSheet(sheetName) {
        const trackerAny = this.tracker;
        if (typeof trackerAny.getLocationsForSheet === 'function') {
            const raw = trackerAny.getLocationsForSheet(sheetName);
            return this.normalizeTrackerSnapshots(sheetName, raw);
        }
        if (typeof trackerAny.getAllLocations === 'function') {
            const raw = trackerAny.getAllLocations();
            const all = this.normalizeTrackerSnapshots(undefined, raw);
            return all.filter((item) => this.normalizeSheetName(item.sheetName) === this.normalizeSheetName(sheetName));
        }
        return null;
    }
    normalizeTrackerSnapshots(fallbackSheetName, raw) {
        if (!Array.isArray(raw)) {
            return [];
        }
        const snapshots = [];
        for (const item of raw) {
            const sheetName = String(item?.sheetName ?? item?.sheet ?? item?.worksheetName ?? fallbackSheetName ?? '').trim();
            const cellAddress = this.stripSheetPrefixFromAddress(String(item?.cellAddress ?? item?.address ?? item?.rangeAddress ?? '').trim());
            if (!sheetName || !cellAddress) {
                continue;
            }
            const mappings = Array.isArray(item?.mappings)
                ? item.mappings
                : Array.isArray(item?.locations)
                    ? item.locations
                    : this.tracker.getLocationsForCell(sheetName, cellAddress);
            if (mappings.length === 0) {
                continue;
            }
            snapshots.push({
                sheetName,
                cellAddress,
                mappings,
            });
        }
        return this.dedupeTrackerSnapshots(snapshots);
    }
    dedupeTrackerSnapshots(snapshots) {
        const byCell = new Map();
        for (const snapshot of snapshots) {
            const key = `${this.normalizeSheetName(snapshot.sheetName)}:${this.normalizeCellAddress(snapshot.cellAddress)}`;
            const existing = byCell.get(key);
            if (!existing) {
                byCell.set(key, {
                    sheetName: snapshot.sheetName,
                    cellAddress: this.normalizeCellAddress(snapshot.cellAddress),
                    mappings: snapshot.mappings,
                });
                continue;
            }
            byCell.set(key, {
                ...existing,
                mappings: [...existing.mappings, ...snapshot.mappings],
            });
        }
        return [...byCell.values()];
    }
    async collectRegisteredCandidatesFromTrackerSnapshots(ctx, sheet, sheetName, snapshots, summary) {
        const candidates = [];
        if (snapshots.length === 0) {
            return candidates;
        }
        const jobs = [];
        for (const snapshot of snapshots) {
            const cellAddress = this.normalizeCellAddress(snapshot.cellAddress);
            try {
                const range = sheet.getRange(cellAddress);
                range.load(['formulas', 'values', 'rowIndex', 'columnIndex']);
                jobs.push({
                    snapshot: {
                        ...snapshot,
                        cellAddress,
                    },
                    range,
                });
            }
            catch (error) {
                summary.skipped.push({
                    sheet: sheetName,
                    address: cellAddress,
                    reason: 'invalid_cell_address',
                    message: `Invalid registered cell address: ${this.errorToMessage(error)}`,
                });
            }
        }
        if (jobs.length === 0) {
            return candidates;
        }
        await ctx.sync();
        for (const job of jobs) {
            summary.cellsScanned++;
            const formula = job.range.formulas?.[0]?.[0];
            if (!formula || typeof formula !== 'string' || !formula.startsWith('=')) {
                continue;
            }
            const calls = this.parseAlphaCalls(formula);
            if (calls.length === 0) {
                continue;
            }
            summary.callsFound += calls.length;
            summary.fastPathCallsAttempted += calls.length;
            const mappings = job.snapshot.mappings.length > 0
                ? job.snapshot.mappings
                : this.tracker.getLocationsForCell(sheetName, job.snapshot.cellAddress);
            if (mappings.length === 0) {
                summary.callsLeftLive += calls.length;
                summary.skipped.push({
                    sheet: sheetName,
                    address: job.snapshot.cellAddress,
                    reason: 'unregistered_alpha_formula_skipped',
                    message: 'This Alpha formula was not registered in the tracker, so it was intentionally left live.',
                });
                continue;
            }
            candidates.push({
                sheetName,
                rowIndex: job.range.rowIndex,
                columnIndex: job.range.columnIndex,
                cellAddress: job.snapshot.cellAddress,
                range: job.range,
                originalFormula: formula,
                currentValue: job.range.values?.[0]?.[0],
                calls,
                mappings,
            });
        }
        return candidates;
    }
    collectRegisteredCandidatesFromLoadedRange(sheetName, range, summary) {
        const formulas = range.formulas;
        const values = range.values;
        const candidates = [];
        for (let row = 0; row < range.rowCount; row++) {
            for (let column = 0; column < range.columnCount; column++) {
                summary.cellsScanned++;
                const formula = formulas?.[row]?.[column];
                if (!formula || typeof formula !== 'string' || !formula.startsWith('=')) {
                    continue;
                }
                const calls = this.parseAlphaCalls(formula);
                if (calls.length === 0) {
                    continue;
                }
                summary.callsFound += calls.length;
                summary.fastPathCallsAttempted += calls.length;
                const absoluteRowIndex = range.rowIndex + row;
                const absoluteColumnIndex = range.columnIndex + column;
                const cellAddress = `${this.columnIndexToName(absoluteColumnIndex)}${absoluteRowIndex + 1}`;
                const mappings = this.tracker.getLocationsForCell(sheetName, cellAddress);
                if (mappings.length === 0) {
                    summary.callsLeftLive += calls.length;
                    summary.skipped.push({
                        sheet: sheetName,
                        address: cellAddress,
                        reason: 'unregistered_alpha_formula_skipped',
                        message: 'This Alpha formula was not registered in the tracker, so it was intentionally left live.',
                    });
                    continue;
                }
                candidates.push({
                    sheetName,
                    rowIndex: absoluteRowIndex,
                    columnIndex: absoluteColumnIndex,
                    cellAddress,
                    range: range.getCell(row, column),
                    originalFormula: formula,
                    currentValue: values?.[row]?.[column],
                    calls,
                    mappings,
                });
            }
        }
        return candidates;
    }
    buildFreezeJobsFromRegisteredCandidates(candidates, summary) {
        const jobs = [];
        for (const candidate of candidates) {
            const tradingDaysMapping = candidate.mappings.find((mapping) => this.isTradingDaysOtherMarketDataMapping(mapping));
            if (tradingDaysMapping) {
                const arrayJob = this.tryBuildTradingDaysArrayJob(candidate, tradingDaysMapping, summary);
                if (arrayJob) {
                    jobs.push(arrayJob);
                }
                continue;
            }
            jobs.push({
                kind: 'scalar',
                sheetName: candidate.sheetName,
                rowIndex: candidate.rowIndex,
                columnIndex: candidate.columnIndex,
                cellAddress: candidate.cellAddress,
                range: candidate.range,
                value: candidate.currentValue,
                originalFormula: candidate.originalFormula,
                alphaCallCount: candidate.calls.length,
            });
        }
        return this.removeScalarJobsCoveredByArrayJobs(jobs);
    }
    tryBuildTradingDaysArrayJob(candidate, mapping, summary) {
        const cached = this.cache.getReadyValueForUnlinkByKey(mapping.key);
        if (!cached.ok) {
            const status = cached.status;
            const reason = status === 'stale'
                ? 'refresh_required_stale'
                : status === 'fetching'
                    ? 'refresh_required_fetching'
                    : status === 'missing'
                        ? 'refresh_required_missing'
                        : 'array_cache_error';
            const message = status === 'stale'
                ? 'This Trading_Days array value is stale. Refresh before unlinking.'
                : status === 'fetching'
                    ? 'This Trading_Days array value is still fetching. Wait for refresh to complete before unlinking.'
                    : status === 'missing'
                        ? 'This Trading_Days array value is missing from cache. Refresh before unlinking.'
                        : `This Trading_Days array value could not be read from cache: ${cached.reason ?? 'unknown error'}`;
            this.addRefreshRequired(summary, {
                sheet: candidate.sheetName,
                address: candidate.cellAddress,
                reason,
                message,
            });
            summary.callsLeftLive += candidate.calls.length;
            return null;
        }
        const matrix = this.toExcelValueMatrix(cached.data);
        if (!matrix.ok) {
            summary.skipped.push({
                sheet: candidate.sheetName,
                address: candidate.cellAddress,
                reason: 'array_cache_value_invalid',
                message: `Trading_Days cache value is not a valid Excel matrix: ${matrix.reason}`,
            });
            summary.callsLeftLive += candidate.calls.length;
            return null;
        }
        return {
            kind: 'array',
            sheetName: candidate.sheetName,
            rowIndex: candidate.rowIndex,
            columnIndex: candidate.columnIndex,
            cellAddress: candidate.cellAddress,
            anchorRange: candidate.range,
            values: this.normalizeMatrixForExcel(matrix.values),
            originalFormula: candidate.originalFormula,
            alphaCallCount: candidate.calls.length,
        };
    }
    removeScalarJobsCoveredByArrayJobs(jobs) {
        const covered = new Set();
        for (const job of jobs) {
            if (job.kind !== 'array')
                continue;
            const rows = job.values.length;
            const cols = job.values[0]?.length ?? 0;
            for (let r = 0; r < rows; r++) {
                for (let c = 0; c < cols; c++) {
                    covered.add(this.cellPositionKey(job.sheetName, job.rowIndex + r, job.columnIndex + c));
                }
            }
        }
        return jobs.filter((job) => {
            if (job.kind === 'array')
                return true;
            const key = this.cellPositionKey(job.sheetName, job.rowIndex, job.columnIndex);
            return !covered.has(key);
        });
    }
    async applyFreezeJobs(ctx, jobs, summary, mappingsToRemove) {
        if (jobs.length === 0)
            return;
        const arrayJobs = jobs.filter((job) => job.kind === 'array');
        const scalarJobs = jobs.filter((job) => job.kind === 'scalar');
        const writesStartedAt = Date.now();
        const arrayNoteInputs = this.queueArrayWrites(arrayJobs, summary, mappingsToRemove);
        const scalarSegments = this.buildBestScalarWriteSegments(scalarJobs);
        const scalarNoteInputs = this.queueScalarSegmentWrites(ctx, scalarSegments, summary, mappingsToRemove);
        await ctx.sync();
        summary.timingsMs.writes += Date.now() - writesStartedAt;
        const noteInputs = [...arrayNoteInputs, ...scalarNoteInputs];
        const notesStartedAt = Date.now();
        await this.applyNotesSafely(ctx, noteInputs, summary);
        summary.timingsMs.notes += Date.now() - notesStartedAt;
    }
    queueArrayWrites(jobs, summary, mappingsToRemove) {
        const noteInputs = [];
        for (const job of jobs) {
            const rows = job.values.length;
            const cols = job.values[0]?.length ?? 0;
            if (rows === 0 || cols === 0) {
                continue;
            }
            const target = job.anchorRange.getResizedRange(rows - 1, cols - 1);
            target.values = job.values;
            const writtenCells = rows * cols;
            summary.cellsChanged += writtenCells;
            summary.formulasFrozen++;
            summary.callsReplaced += job.alphaCallCount;
            summary.arrayCallsReplaced += job.alphaCallCount;
            summary.arrayCellsWritten += writtenCells;
            summary.callsResolvedByFastPath += job.alphaCallCount;
            summary.fastPathCallsResolved += job.alphaCallCount;
            mappingsToRemove.push({
                sheet: job.sheetName,
                address: job.cellAddress,
            });
            noteInputs.push({
                sheetName: job.sheetName,
                range: job.anchorRange,
                cellAddress: job.cellAddress,
                noteText: `Replaced AlphaScript formula:\n${job.originalFormula}\n\n` +
                    `Note: This Trading_Days array output was frozen from the cached Alpha value.`,
            });
        }
        return noteInputs;
    }
    buildBestScalarWriteSegments(jobs) {
        if (jobs.length === 0) {
            return [];
        }
        const horizontal = this.buildHorizontalScalarWriteSegments(jobs);
        const vertical = this.buildVerticalScalarWriteSegments(jobs);
        return vertical.length < horizontal.length ? vertical : horizontal;
    }
    buildHorizontalScalarWriteSegments(jobs) {
        const bySheetRow = new Map();
        for (const job of jobs) {
            const key = `${job.sheetName}:${job.rowIndex}`;
            const bucket = bySheetRow.get(key) ?? [];
            bucket.push(job);
            bySheetRow.set(key, bucket);
        }
        const segments = [];
        for (const [, rowJobs] of bySheetRow) {
            rowJobs.sort((a, b) => a.columnIndex - b.columnIndex);
            let current = [];
            const flush = () => {
                if (current.length === 0)
                    return;
                segments.push({
                    orientation: 'horizontal',
                    sheetName: current[0].sheetName,
                    rowIndex: current[0].rowIndex,
                    startColumnIndex: current[0].columnIndex,
                    values: [current.map((job) => this.normalizeValueForExcel(job.value))],
                    jobs: current,
                });
                current = [];
            };
            for (const job of rowJobs) {
                const previous = current[current.length - 1];
                if (!previous || job.columnIndex === previous.columnIndex + 1) {
                    current.push(job);
                }
                else {
                    flush();
                    current.push(job);
                }
            }
            flush();
        }
        return segments;
    }
    buildVerticalScalarWriteSegments(jobs) {
        const bySheetColumn = new Map();
        for (const job of jobs) {
            const key = `${job.sheetName}:${job.columnIndex}`;
            const bucket = bySheetColumn.get(key) ?? [];
            bucket.push(job);
            bySheetColumn.set(key, bucket);
        }
        const segments = [];
        for (const [, columnJobs] of bySheetColumn) {
            columnJobs.sort((a, b) => a.rowIndex - b.rowIndex);
            let current = [];
            const flush = () => {
                if (current.length === 0)
                    return;
                segments.push({
                    orientation: 'vertical',
                    sheetName: current[0].sheetName,
                    startRowIndex: current[0].rowIndex,
                    columnIndex: current[0].columnIndex,
                    values: current.map((job) => [this.normalizeValueForExcel(job.value)]),
                    jobs: current,
                });
                current = [];
            };
            for (const job of columnJobs) {
                const previous = current[current.length - 1];
                if (!previous || job.rowIndex === previous.rowIndex + 1) {
                    current.push(job);
                }
                else {
                    flush();
                    current.push(job);
                }
            }
            flush();
        }
        return segments;
    }
    queueScalarSegmentWrites(ctx, segments, summary, mappingsToRemove) {
        const noteInputs = [];
        for (const segment of segments) {
            const sheet = ctx.workbook.worksheets.getItem(segment.sheetName);
            if (segment.orientation === 'horizontal') {
                const target = sheet.getRangeByIndexes(segment.rowIndex, segment.startColumnIndex, 1, segment.values[0].length);
                target.values = segment.values;
            }
            else {
                const target = sheet.getRangeByIndexes(segment.startRowIndex, segment.columnIndex, segment.values.length, 1);
                target.values = segment.values;
            }
            for (const job of segment.jobs) {
                summary.cellsChanged++;
                summary.formulasFrozen++;
                summary.callsReplaced += job.alphaCallCount;
                summary.scalarCallsReplaced += job.alphaCallCount;
                summary.callsResolvedByFastPath += job.alphaCallCount;
                summary.fastPathCallsResolved += job.alphaCallCount;
                mappingsToRemove.push({
                    sheet: job.sheetName,
                    address: job.cellAddress,
                });
                noteInputs.push({
                    sheetName: job.sheetName,
                    range: job.range,
                    cellAddress: job.cellAddress,
                    noteText: `Replaced AlphaScript formula:\n${job.originalFormula}`,
                });
            }
        }
        return noteInputs;
    }
    async applyNotesSafely(ctx, noteInputs, summary) {
        if (noteInputs.length === 0)
            return;
        summary.notesAttempted += noteInputs.length;
        if (!Office.context.requirements.isSetSupported('ExcelApi', '1.18')) {
            for (const input of noteInputs) {
                summary.notesFailed.push({
                    sheet: input.sheetName,
                    address: input.cellAddress,
                    reason: 'ExcelApi 1.18 notes API is not supported.',
                });
            }
            return;
        }
        for (let i = 0; i < noteInputs.length; i += this.NOTE_CHUNK_SIZE) {
            const chunk = noteInputs.slice(i, i + this.NOTE_CHUNK_SIZE);
            try {
                await this.upsertNoteChunk(ctx, chunk);
                summary.notesSucceeded += chunk.length;
            }
            catch {
                for (const item of chunk) {
                    try {
                        await this.upsertNoteChunk(ctx, [item]);
                        summary.notesSucceeded++;
                    }
                    catch (singleError) {
                        const reason = this.errorToMessage(singleError);
                        summary.notesFailed.push({
                            sheet: item.sheetName,
                            address: item.cellAddress,
                            reason,
                        });
                        summary.skipped.push({
                            sheet: item.sheetName,
                            address: item.cellAddress,
                            reason: 'note_failed',
                            message: `Formula was unlinked, but note creation failed: ${reason}`,
                        });
                    }
                }
            }
        }
    }
    async upsertNoteChunk(ctx, inputs) {
        const jobs = [];
        for (const input of inputs) {
            const note = input.range.worksheet.notes.getItemOrNullObject(input.cellAddress);
            note.load('isNullObject');
            jobs.push({
                note,
                range: input.range,
                cellAddress: input.cellAddress,
                noteText: input.noteText,
            });
        }
        await ctx.sync();
        for (const job of jobs) {
            if (job.note.isNullObject) {
                job.range.worksheet.notes.add(job.cellAddress, job.noteText);
            }
            else {
                job.note.content = job.noteText;
            }
        }
        await ctx.sync();
    }
    async verifyRangeIfEnabled(ctx, sheetName, range, summary) {
        if (!this.ENABLE_VERIFICATION)
            return;
        const verificationStartedAt = Date.now();
        range.load(['rowCount', 'columnCount', 'rowIndex', 'columnIndex', 'formulas']);
        await ctx.sync();
        this.verifyLoadedRangeHasNoRegisteredAlphaFormulas(sheetName, range, summary);
        summary.timingsMs.verification += Date.now() - verificationStartedAt;
    }
    verifyLoadedRangeHasNoRegisteredAlphaFormulas(sheetName, range, summary) {
        const formulas = range.formulas;
        for (let row = 0; row < range.rowCount; row++) {
            for (let column = 0; column < range.columnCount; column++) {
                summary.verificationCellsScanned++;
                const formula = formulas?.[row]?.[column];
                if (!formula || typeof formula !== 'string' || !formula.startsWith('=')) {
                    continue;
                }
                const calls = this.parseAlphaCalls(formula);
                if (calls.length === 0) {
                    continue;
                }
                const absoluteRowIndex = range.rowIndex + row;
                const absoluteColumnIndex = range.columnIndex + column;
                const address = `${this.columnIndexToName(absoluteColumnIndex)}${absoluteRowIndex + 1}`;
                const mappings = this.tracker.getLocationsForCell(sheetName, address);
                if (mappings.length === 0) {
                    continue;
                }
                summary.verificationAlphaCellsFound++;
                summary.remainingAlphaFormulaCells.push({
                    sheet: sheetName,
                    address,
                    formula,
                });
                summary.skipped.push({
                    sheet: sheetName,
                    address,
                    reason: 'remaining_alpha_formula',
                    message: 'This registered Alpha formula still remains after unlink.',
                });
            }
        }
    }
    isTradingDaysOtherMarketDataMapping(mapping) {
        const functionName = this.normalizeShortFunctionName(mapping.functionName);
        const key = String(mapping.key ?? '').trim();
        if (functionName !== 'other_market_data') {
            return false;
        }
        return this.keyContainsSegmentCaseInsensitive(key, 'trading_days');
    }
    keyContainsSegmentCaseInsensitive(key, segment) {
        const normalizedSegment = segment.trim().toLowerCase();
        return key
            .split('|')
            .map((part) => part.trim().toLowerCase())
            .includes(normalizedSegment);
    }
    normalizeShortFunctionName(name) {
        const raw = String(name ?? '').trim();
        const withoutAlphaPrefix = raw.toUpperCase().startsWith('ALPHA.')
            ? raw.slice('ALPHA.'.length)
            : raw;
        return withoutAlphaPrefix
            .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
            .replace(/[-\s]+/g, '_')
            .toLowerCase();
    }
    parseAlphaCalls(formula) {
        const calls = [];
        let i = 0;
        let inString = false;
        while (i < formula.length) {
            const ch = formula[i];
            if (ch === '"') {
                if (inString && formula[i + 1] === '"') {
                    i += 2;
                    continue;
                }
                inString = !inString;
                i++;
                continue;
            }
            if (inString) {
                i++;
                continue;
            }
            if (!this.startsWithAlphaPrefix(formula, i)) {
                i++;
                continue;
            }
            const nameEnd = this.readFunctionNameEnd(formula, i);
            if (nameEnd === i) {
                i++;
                continue;
            }
            const functionName = formula.slice(i, nameEnd);
            if (formula[nameEnd] !== '(') {
                i++;
                continue;
            }
            const closeParen = this.findMatchingParen(formula, nameEnd);
            if (closeParen === -1) {
                i++;
                continue;
            }
            calls.push({
                functionName,
                start: i,
                end: closeParen + 1,
                text: formula.slice(i, closeParen + 1),
            });
            i = closeParen + 1;
        }
        return calls;
    }
    startsWithAlphaPrefix(formula, index) {
        return formula.slice(index, index + 'ALPHA.'.length).toUpperCase() === 'ALPHA.';
    }
    readFunctionNameEnd(formula, start) {
        let i = start;
        while (i < formula.length) {
            const ch = formula[i];
            if (/[A-Za-z0-9_.]/.test(ch)) {
                i++;
                continue;
            }
            break;
        }
        return i;
    }
    findMatchingParen(formula, openParenIndex) {
        let depth = 0;
        let inString = false;
        for (let i = openParenIndex; i < formula.length; i++) {
            const ch = formula[i];
            if (ch === '"') {
                if (inString && formula[i + 1] === '"') {
                    i++;
                    continue;
                }
                inString = !inString;
                continue;
            }
            if (inString)
                continue;
            if (ch === '(') {
                depth++;
                continue;
            }
            if (ch === ')') {
                depth--;
                if (depth === 0) {
                    return i;
                }
            }
        }
        return -1;
    }
    toExcelValueMatrix(value) {
        if (!Array.isArray(value)) {
            return { ok: false, reason: 'not_array_value' };
        }
        if (value.length === 0) {
            return { ok: false, reason: 'empty_array_value' };
        }
        if (Array.isArray(value[0])) {
            const rows = value;
            const width = rows[0]?.length ?? 0;
            if (width === 0) {
                return { ok: false, reason: 'empty_array_row' };
            }
            const rectangular = rows.every((row) => Array.isArray(row) && row.length === width);
            if (!rectangular) {
                return { ok: false, reason: 'non_rectangular_array' };
            }
            return { ok: true, values: rows };
        }
        return {
            ok: true,
            values: value.map((item) => [item]),
        };
    }
    normalizeValueForExcel(value) {
        if (value === undefined)
            return null;
        return value;
    }
    normalizeMatrixForExcel(values) {
        return values.map((row) => row.map((value) => this.normalizeValueForExcel(value)));
    }
    cleanupTrackerMappings(cells) {
        if (cells.length > 0) {
            this.tracker.removeMappingsForCells(cells);
        }
    }
    addRefreshRequired(summary, issue) {
        summary.refreshRequired.push(issue);
        summary.skipped.push(issue);
    }
    finalizeSummary(summary) {
        if (summary.refreshRequired.length > 0 || summary.notesFailed.length > 0) {
            summary.status = summary.cellsChanged > 0 ? 'partial' : 'failed';
            return summary;
        }
        summary.status = 'complete';
        return summary;
    }
    createSummary() {
        return {
            status: 'failed',
            cellsScanned: 0,
            cellsChanged: 0,
            callsFound: 0,
            callsReplaced: 0,
            callsLeftLive: 0,
            scalarCallsReplaced: 0,
            arrayCallsReplaced: 0,
            arrayCellsWritten: 0,
            formulasFrozen: 0,
            errorValuesFrozen: 0,
            callsResolvedByFastPath: 0,
            callsResolvedByScratch: 0,
            fastPathCallsAttempted: 0,
            fastPathCallsResolved: 0,
            scratchCallsAttempted: 0,
            scratchCallsTimedOut: 0,
            notesAttempted: 0,
            notesSucceeded: 0,
            notesFailed: [],
            verificationCellsScanned: 0,
            verificationAlphaCellsFound: 0,
            timingsMs: {
                total: 0,
                fastPath: 0,
                scratch: 0,
                writes: 0,
                notes: 0,
                verification: 0,
            },
            refreshRequired: [],
            frozenFallbacks: [],
            skipped: [],
            remainingAlphaFormulaCells: [],
        };
    }
    isCellAddressInsideBounds(cellAddress, bounds) {
        const parsed = this.parseCellAddress(cellAddress);
        if (!parsed) {
            return false;
        }
        return (parsed.rowIndex >= bounds.rowIndex &&
            parsed.rowIndex < bounds.rowIndex + bounds.rowCount &&
            parsed.columnIndex >= bounds.columnIndex &&
            parsed.columnIndex < bounds.columnIndex + bounds.columnCount);
    }
    parseCellAddress(cellAddress) {
        const normalized = this.normalizeCellAddress(cellAddress);
        const match = normalized.match(/^\$?([A-Z]+)\$?(\d+)$/i);
        if (!match) {
            return null;
        }
        return {
            columnIndex: this.columnNameToIndex(match[1]),
            rowIndex: Number(match[2]) - 1,
        };
    }
    normalizeSheetName(sheetName) {
        return String(sheetName ?? '').trim().toLowerCase();
    }
    normalizeCellAddress(address) {
        return this.stripSheetPrefixFromAddress(address)
            .replace(/\$/g, '')
            .trim()
            .toUpperCase();
    }
    stripSheetPrefixFromAddress(address) {
        const raw = String(address ?? '').trim();
        const bangIndex = raw.lastIndexOf('!');
        if (bangIndex >= 0) {
            return raw.slice(bangIndex + 1).replace(/^'|'$/g, '');
        }
        return raw.replace(/^'|'$/g, '');
    }
    cellPositionKey(sheetName, rowIndex, columnIndex) {
        return `${this.normalizeSheetName(sheetName)}:${rowIndex}:${columnIndex}`;
    }
    columnNameToIndex(name) {
        let result = 0;
        for (const ch of name.toUpperCase()) {
            result = result * 26 + (ch.charCodeAt(0) - 64);
        }
        return result - 1;
    }
    columnIndexToName(index) {
        let dividend = index + 1;
        let columnName = '';
        while (dividend > 0) {
            const modulo = (dividend - 1) % 26;
            columnName = String.fromCharCode(65 + modulo) + columnName;
            dividend = Math.floor((dividend - modulo) / 26);
        }
        return columnName;
    }
    errorToMessage(error) {
        if (error instanceof Error)
            return error.message;
        try {
            return JSON.stringify(error);
        }
        catch {
            return String(error);
        }
    }
}
export async function unlinkSelectedRangeAlphaFormulas() {
    return FormulaUnlinker.getInstance().unlinkSelectedRangeAlphaFormulas();
}
export async function unlinkActiveWorksheetAlphaFormulas() {
    return FormulaUnlinker.getInstance().unlinkActiveWorksheetAlphaFormulas();
}
export async function unlinkWorkbookAlphaFormulas() {
    return FormulaUnlinker.getInstance().unlinkWorkbookAlphaFormulas();
}
//# sourceMappingURL=formulaUnlinker.js.map